/**
 * Page Type Mapping Configuration
 * Maps URLs to page_type and page_name for PostHog $pageview events
 */

export type PageType =
  | 'home'
  | 'pricing'
  | 'product'
  | 'solution'
  | 'company'
  | 'resource'
  | 'legal'
  | 'blog'
  | 'customer_story'
  | 'coverage'
  | 'error'
  | 'unknown';

export interface PageInfo {
  page_type: PageType;
  page_name: string;
}

interface StaticPageMapping {
  url: string;
  page_name: string;
}

interface DynamicPagePattern {
  pattern: RegExp;
  page_type: PageType;
  extractPageName: (url: string, match: RegExpMatchArray) => string;
}

// Static page mappings by page_type
const STATIC_PAGES: Record<PageType, StaticPageMapping[]> = {
  home: [
    { url: '/', page_name: 'home' }
  ],
  pricing: [
    { url: '/pricing/', page_name: 'pricing' }
  ],
  product: [
    { url: '/business-accounts/', page_name: 'business_accounts' },
    { url: '/business-accounts/global-account/', page_name: 'global_account' },
    { url: '/business-accounts/local-account/', page_name: 'local_account' },
    { url: '/business-accounts/multi-currency-wallet/', page_name: 'multi_currency_wallet' },
    { url: '/business-accounts/v-pay/', page_name: 'v_pay' },
    { url: '/business-accounts/international-payments/', page_name: 'international_payments' },
    { url: '/business-accounts/local-payments/', page_name: 'local_payments' },
    { url: '/invoice-payments/payment-links/', page_name: 'payment_links' },
    { url: '/business-accounts/virtual-card/', page_name: 'virtual_card' },
    { url: '/business-accounts/physical-card/', page_name: 'physical_card' },
    { url: '/business-accounts/treasury-management/', page_name: 'treasury_management' },
    { url: '/fx/', page_name: 'fx' },
    { url: '/fx/spot-fx/', page_name: 'spot_fx' },
    { url: '/fx/otc-fx/', page_name: 'otc_fx' },
    { url: '/fx/marketplace/', page_name: 'fx_marketplace' },
    { url: '/fx/auto-exchange/', page_name: 'auto_exchange' },
    { url: '/atlas/', page_name: 'atlas' },
    { url: '/atlas/collections-and-accounts/', page_name: 'atlas_collections' },
    { url: '/atlas/apis/', page_name: 'atlas_apis' }
  ],
  solution: [
    { url: '/solutions/company-size/start-up/', page_name: 'startups' },
    { url: '/solutions/company-size/scale-up/', page_name: 'scale_up' },
    { url: '/solutions/company-size/enterprises/', page_name: 'enterprises' },
    { url: '/solutions/use-cases/creator-economy/', page_name: 'creator_economy' },
    { url: '/solutions/use-cases/global-payroll/', page_name: 'global_payroll' },
    { url: '/solutions/use-cases/construction/', page_name: 'construction' },
    { url: '/solutions/use-cases/logistics/', page_name: 'logistics' },
    { url: '/solutions/use-cases/marketplaces/', page_name: 'marketplaces_solution' },
    { url: '/solutions/use-cases/manufacturing/', page_name: 'manufacturing' },
    { url: '/solutions/use-cases/online-retail/', page_name: 'online_retail' },
    { url: '/solutions/industries/charities-and-non-govermental-organisations/', page_name: 'charities_ngos' },
    { url: '/solutions/industries/educational-institutions/', page_name: 'educational_institutions' },
    { url: '/solutions/industries/exporters/', page_name: 'exporters' },
    { url: '/solutions/industries/health&pharmaceuticals/', page_name: 'health_pharmaceuticals' },
    { url: '/solutions/industries/importers/', page_name: 'importers' },
    { url: '/solutions/industries/telecommunications/', page_name: 'media_communications' },
    { url: '/solutions/industries/travel-and-tourism/', page_name: 'travel_tourism' },
    { url: '/solutions/industries/energyandcommodities/', page_name: 'energy_commodities' },
    { url: '/solutions/industries/trade/', page_name: 'trade' }
  ],
  company: [
    { url: '/about-us/company/about-us/', page_name: 'about' },
    { url: '/about-us/company/careers/', page_name: 'careers' },
    { url: '/about-us/company/partners/', page_name: 'partners' },
    { url: '/about-us/company/licences/', page_name: 'licences' }
  ],
  resource: [
    { url: '/about-us/resources/support/', page_name: 'support' },
    { url: '/about-us/resources/whitepapers-and-reports/', page_name: 'whitepapers_index' },
    { url: '/about-us/media-and-events/webinars-and-podcasts/', page_name: 'webinars_index' },
    { url: '/about-us/media-and-events/events/', page_name: 'events_index' },
    { url: '/about-us/media-and-events/newsroom/', page_name: 'newsroom' },
    { url: '/about-us/resources/sitemap/', page_name: 'sitemap' }
  ],
  legal: [
    { url: '/about-us/legal/privacy-policy/', page_name: 'privacy_policy' },
    { url: '/about-us/legal/terms-of-service/', page_name: 'terms_of_service' },
    { url: '/about-us/legal/cookie-policy/', page_name: 'cookie_policy' }
  ],
  blog: [
    { url: '/about-us/resources/blog/', page_name: 'blog_index' }
  ],
  customer_story: [
    { url: '/about-us/media-and-events/customer-stories/', page_name: 'customer_stories_index' }
  ],
  coverage: [
    { url: '/coverage/currencies/', page_name: 'currencies_index' },
    { url: '/coverage/countries/', page_name: 'countries_index' }
  ],
  error: [],
  unknown: []
};

// Dynamic page patterns for slug-based URLs
const DYNAMIC_PATTERNS: DynamicPagePattern[] = [
  // Blog articles: /about-us/resources/blog/article/[slug]
  {
    pattern: /^\/about-us\/resources\/blog\/article\/([^\/]+)\/?$/,
    page_type: 'blog',
    extractPageName: (_url, match) => toSnakeCase(match[1])
  },
  // Customer stories: /about-us/media-and-events/customer-stories/[slug]
  {
    pattern: /^\/about-us\/media-and-events\/customer-stories\/([^\/]+)\/?$/,
    page_type: 'customer_story',
    extractPageName: (_url, match) => toSnakeCase(match[1])
  },
  // Webinars: /about-us/media-and-events/webinars-and-podcasts/[slug]
  {
    pattern: /^\/about-us\/media-and-events\/webinars-and-podcasts\/([^\/]+)\/?$/,
    page_type: 'resource',
    extractPageName: (_url, match) => toSnakeCase(match[1])
  },
  // Currency pages: /coverage/currencies/[region]/[slug]
  {
    pattern: /^\/coverage\/currencies\/[^\/]+\/([^\/]+)\/?$/,
    page_type: 'coverage',
    extractPageName: (_url, match) => toSnakeCase(match[1])
  },
  // Country pages: /coverage/countries/[region]/[slug]
  {
    pattern: /^\/coverage\/countries\/[^\/]+\/([^\/]+)\/?$/,
    page_type: 'coverage',
    extractPageName: (_url, match) => toSnakeCase(match[1])
  }
];

/**
 * Converts a string to snake_case
 * Example: "global-payments" → "global_payments"
 */
function toSnakeCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Normalizes URL path for consistent matching
 * - Removes query params and hash
 * - Ensures trailing slash for consistency
 */
function normalizeUrl(url: string): string {
  // Remove query params and hash
  let path = url.split('?')[0].split('#')[0];
  // Ensure trailing slash (except for root)
  if (path !== '/' && !path.endsWith('/')) {
    path += '/';
  }
  return path;
}

/**
 * Build a lookup map for static pages for O(1) lookup
 */
const staticPageLookup: Map<string, PageInfo> = new Map();

// Populate the lookup map
for (const [pageType, pages] of Object.entries(STATIC_PAGES)) {
  for (const page of pages) {
    staticPageLookup.set(page.url, {
      page_type: pageType as PageType,
      page_name: page.page_name
    });
  }
}

/**
 * Gets page_type and page_name from URL
 * Checks static mappings first, then dynamic patterns, then falls back to unknown
 */
export function getPageInfo(url: string): PageInfo {
  const normalizedUrl = normalizeUrl(url);

  // Check static pages first (O(1) lookup)
  const staticPage = staticPageLookup.get(normalizedUrl);
  if (staticPage) {
    return staticPage;
  }

  // Check dynamic patterns
  for (const pattern of DYNAMIC_PATTERNS) {
    const match = normalizedUrl.match(pattern.pattern);
    if (match) {
      return {
        page_type: pattern.page_type,
        page_name: pattern.extractPageName(normalizedUrl, match)
      };
    }
  }

  // Fallback to unknown - derive page_name from URL
  const segments = normalizedUrl.split('/').filter(s => s);
  const lastSegment = segments[segments.length - 1] || 'unknown';
  
  return {
    page_type: 'unknown',
    page_name: toSnakeCase(lastSegment)
  };
}

/**
 * Special case for 404 error page
 * Call this explicitly from the 404 component
 */
export function getErrorPageInfo(): PageInfo {
  return {
    page_type: 'error',
    page_name: '404'
  };
}
